import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { 
  Box, 
  Typography, 
  IconButton, 
  Menu, 
  MenuItem, 
  Card, 
  CardMedia, 
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  ListItemIcon,
  ListItemText,
  Divider,
  CircularProgress
} from '@mui/material';
import { styled } from '@mui/system';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import HomeIcon from '@mui/icons-material/Home';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import LayersClearIcon from '@mui/icons-material/LayersClear';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { supabase } from '../supabaseClient';

const PropertyCard = styled(Card)(({ theme }) => ({
  borderRadius: '12px',
  overflow: 'hidden',
  boxShadow: theme.palette.mode === 'dark' 
    ? '0 4px 20px rgba(0, 0, 0, 0.3)' 
    : '0 4px 20px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s ease',
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.palette.mode === 'dark' 
      ? '0 8px 30px rgba(0, 0, 0, 0.4)' 
      : '0 8px 30px rgba(0, 0, 0, 0.15)',
  },
}));

const ImageContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  paddingTop: '75%',
  cursor: 'pointer',
  overflow: 'hidden',
}));

const StyledImage = styled('img')(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  backgroundColor: theme.palette.mode === 'dark' ? '#1A1A25' : '#f5f5f5',
  transition: 'all 0.3s ease-in-out',
  opacity: 1,
  '&:hover': {
    filter: 'brightness(1.1) contrast(1.1)',
  },
}));

const PropertyGallery = () => {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [newPropertyName, setNewPropertyName] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const fetchProperties = useCallback(async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/signup', {
          state: { 
            returnUrl: '/properties',
            message: 'Mülklerinizi görüntülemek için lütfen giriş yapın veya kaydolun'
          }
        });
        return;
      }

      // Get properties directly from properties table
      const { data, error } = await supabase
        .from('properties')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Process the properties data
      const propertiesData = data.map(property => ({
        id: property.id,
        name: property.name || 'Unnamed Property',
        thumbnailUrl: property.thumbnail_url || '/placeholder-image.png',
        createdAt: property.created_at
      }));

      console.log('Fetched properties:', propertiesData);
      setProperties(propertiesData);
    } catch (error) {
      console.error('Error fetching properties:', error);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  const handleMenuOpen = (event, property) => {
    setAnchorEl(event.currentTarget);
    setSelectedProperty(property);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handlePropertyClick = (property) => {
    navigate(`/property/${property.id}`);
  };

  const handleRenameClick = () => {
    setNewPropertyName(selectedProperty?.name || '');
    setRenameDialogOpen(true);
    handleMenuClose();
  };

  const handleRenameConfirm = async () => {
    if (!newPropertyName.trim() || !selectedProperty) return;

    try {
      // Update property name in properties table
      const { error: propertyError } = await supabase
        .from('properties')
        .update({ name: newPropertyName })
        .eq('id', selectedProperty.id);

      if (propertyError) throw propertyError;
      
      // Update property name in all images with this property_id
      const { error } = await supabase
        .from('images')
        .update({ property_name: newPropertyName })
        .eq('property_id', selectedProperty.id);

      if (error) throw error;
      
      // Update local state
      setProperties(prev => 
        prev.map(p => p.id === selectedProperty.id 
          ? { ...p, name: newPropertyName } 
          : p
        )
      );
      
      setRenameDialogOpen(false);
    } catch (error) {
      console.error('Error renaming property:', error);
    }
  };

  const handleDeleteClick = () => {
    setDeleteConfirmOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    if (!selectedProperty?.id) return;

    try {
      // First remove property references from images
      const { error: updateError } = await supabase
        .from('images')
        .update({ 
          property_id: null,
          property_name: null
        })
        .eq('property_id', selectedProperty.id);

      if (updateError) throw updateError;

      // Then delete the property itself
      const { error: deleteError } = await supabase
        .from('properties')
        .delete()
        .eq('id', selectedProperty.id);

      if (deleteError) throw deleteError;
      
      // Update local state
      setProperties(prev => prev.filter(p => p.id !== selectedProperty.id));
      
      setDeleteConfirmOpen(false);
      setSelectedProperty(null);
    } catch (error) {
      console.error('Error deleting property:', error);
    }
  };

  const handleAddProperty = async () => {
    try {
      // Generate a new property ID
      const propertyId = uuidv4();
      const defaultName = "Yeni Mülk";
      
      // Create the property in the properties table
      const { data: { user } } = await supabase.auth.getUser();
      
      const { error } = await supabase
        .from('properties')
        .insert([{
          id: propertyId,
          name: defaultName,
          user_id: user.id,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }]);
        
      if (error) throw error;
      
      // Navigate to canvas editor with the new property ID
      navigate('/canvas-editor', { 
        state: { 
          createNewProperty: true,
          propertyId: propertyId,
          propertyName: defaultName
        } 
      });
    } catch (error) {
      console.error('Error creating property:', error);
      alert('Mülk oluşturulurken bir hata oluştu. Lütfen tekrar deneyin.');
      // Still navigate even if there's an error
      navigate('/canvas-editor', { state: { createNewProperty: true } });
    }
  };

  return (
    <Box sx={{ 
      width: '100%',
      maxWidth: '1600px', 
      margin: '0 auto', 
      padding: '30px 20px',
      position: 'relative',
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      background: theme => `radial-gradient(circle at 50% 50%, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
      borderRadius: '24px',
      boxShadow: theme => `0 8px 32px ${theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.15)'}`,
      border: theme => `1px solid ${theme.palette.divider}`,
      overflow: 'hidden',
    }}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center',
        mb: 4
      }}>
        <IconButton 
          onClick={() => navigate(-1)}
          sx={{ mr: 2 }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Mülklerim
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
          <CircularProgress />
        </Box>
      ) : properties.length === 0 ? (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          height: 300,
          textAlign: 'center',
          p: 3
        }}>
          <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
            Henüz hiç mülk eklenmemiş
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Mülkleriniz burada görüntülenecektir
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 3 }}>
          {properties.map((property) => (
            <PropertyCard key={property.id}>
              <ImageContainer onClick={() => handlePropertyClick(property)}>
                {property.thumbnailUrl ? (
                  <StyledImage 
                    src={property.thumbnailUrl} 
                    alt={property.name}
                    loading="lazy"
                  />
                ) : (
                  <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: theme => theme.palette.mode === 'dark' ? '#1A1A25' : '#f5f5f5',
                    color: 'text.secondary'
                  }}>
                    <HomeIcon sx={{ fontSize: 40, opacity: 0.5 }} />
                  </Box>
                )}
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleMenuOpen(e, property);
                  }}
                  className="menu-button"
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    },
                    zIndex: 10
                  }}
                >
                  <MoreVertIcon />
                </IconButton>
              </ImageContainer>
              <CardContent>
                <Typography variant="h6" noWrap sx={{ fontWeight: 500 }}>
                  {property.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {new Date(property.createdAt).toLocaleDateString()} - Güncellendi
                </Typography>
              </CardContent>
            </PropertyCard>
          ))}
        </Box>
      )}

      {/* Property Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            mt: 1.5,
            width: 220,
            '& .MuiMenuItem-root': {
              px: 2,
              py: 1.5,
              borderRadius: '8px',
              mb: 0.5,
            },
          },
        }}
      >
        <MenuItem onClick={() => {
          handlePropertyClick(selectedProperty);
          handleMenuClose();
        }}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Görüntüle</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={() => {
          navigate(`/canvas-editor`, { state: { propertyId: selectedProperty?.id } });
          handleMenuClose();
        }}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Düzenle</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={handleRenameClick}>
          <ListItemIcon>
            <DriveFileRenameOutlineIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Yeniden Adlandır</ListItemText>
        </MenuItem>
        
        <Divider sx={{ my: 1 }} />
        
        <MenuItem onClick={() => {
          // Handle download source image
          if (selectedProperty?.thumbnailUrl) {
            // Create a temporary anchor element
            const link = document.createElement('a');
            
            // Fetch the image first to handle CORS issues
            fetch(selectedProperty.thumbnailUrl)
              .then(response => response.blob())
              .then(blob => {
                // Create a blob URL for the image
                const blobUrl = URL.createObjectURL(blob);
                
                // Set up the download
                link.href = blobUrl;
                link.download = `${selectedProperty.name || 'property'}.png`;
                document.body.appendChild(link);
                link.click();
                
                // Clean up
                setTimeout(() => {
                  document.body.removeChild(link);
                  URL.revokeObjectURL(blobUrl);
                }, 100);
              })
              .catch(error => {
                console.error('Error downloading image:', error);
                // Fallback to opening in new tab
                window.open(selectedProperty.thumbnailUrl, '_blank');
              });
          }
          handleMenuClose();
        }}>
          <ListItemIcon>
            <FileDownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Kaynak Resmi İndir</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Sil</ListItemText>
        </MenuItem>
      </Menu>

      {/* Rename Dialog */}
      <Dialog open={renameDialogOpen} onClose={() => setRenameDialogOpen(false)}>
        <DialogTitle>Mülkü Yeniden Adlandır</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Mülk Adı"
            type="text"
            fullWidth
            value={newPropertyName}
            onChange={(e) => setNewPropertyName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameDialogOpen(false)}>İptal</Button>
          <Button onClick={handleRenameConfirm} variant="contained">Kaydet</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Mülkü Sil</DialogTitle>
        <DialogContent>
          <Typography>
            "{selectedProperty?.name}" mülkünü silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>İptal</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">Sil</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PropertyGallery;
